import React, { type ComponentPropsWithoutRef, forwardRef, type MouseEvent, type ReactNode, useMemo } from 'react';

import { classNames } from '../../../../helpers/text';
import { Flex } from '../../../Flex';
import { Text } from '../../../Text';
import { Styled as CheckboxStyled } from '../../Checkbox/styles';
import { useSelectContext } from '../context';
import { Styled } from '../styles';

type SelectOptionProps<T> = {
  value?: T | null;
  label?: ReactNode;
  isPlaceholder?: boolean;
  disabled?: boolean;
  index?: number;
};

type Props<T> = Omit<ComponentPropsWithoutRef<'div'>, 'children'> & SelectOptionProps<T>;

export const SelectOptionMulti = forwardRef<HTMLDivElement, Props<any>>(
  ({ value, label, isPlaceholder, index, className, disabled, ...props }, ref) => {
    const { values: selectedValues, formatLabel, onChange, indexOfFocusedItem } = useSelectContext<any>();
    const labelFromValue = formatLabel(value);

    const isSelected = useMemo(() => {
      if (value === undefined || !selectedValues) return false;

      if (typeof value === 'string') return selectedValues.includes(value);

      return selectedValues.some(v => {
        const fieldName = Object.prototype.hasOwnProperty.call(value, 'id') ? 'id' : 'value';

        return v[fieldName] === value[fieldName];
      });
    }, [selectedValues, value]);

    const handleClick = (event: MouseEvent) => {
      if (isPlaceholder) {
        return event.preventDefault();
      }

      const target = event.target as HTMLInputElement;
      target.blur();

      onChange(value, index);
    };

    return (
      <Styled.DropdownOption
        {...props}
        className={classNames(isSelected && 'focus', indexOfFocusedItem === index && 'hover', className)}
        ref={ref}
        role="option"
        aria-selected={isSelected}
        onClick={handleClick}
        onFocus={e => e.stopPropagation()}
        isPlaceholder={isPlaceholder}
        disabled={disabled}
      >
        <Flex.Row space="s">
          <Styled.CheckboxContainer>
            <CheckboxStyled.Input size="s" disabled={disabled} type="checkbox" checked={isSelected} readOnly />
            <CheckboxStyled.Box size="s" />
          </Styled.CheckboxContainer>
          <Text variant="body-lighter">{label ?? labelFromValue}</Text>
        </Flex.Row>
      </Styled.DropdownOption>
    );
  },
);
SelectOptionMulti.displayName = 'Select.OptionMulti';
